import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, CircularProgress } from '@mui/material';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import {
  ApiCurrency,
  ApiPaymentPoint,
  currencyExchange,
  paymentPoints,
} from '../api';
import remainingPointIcon from '../assets/icons/remaining-point.svg';
import spentPointIcon from '../assets/icons/spent-point.svg';
import totalPointIcon from '../assets/icons/total-point.svg';
import { PointDisplay } from '../components/PointDisplay';
import { AuthContext } from '../context/AuthContext';

export const UsePointsDialogContainer = (props: {
  open: boolean;
  salesIndividualId: number;
  reservedPoints: {
    salesIndividualId: number;
    points: number;
  }[];
  onClose: () => void;
  onUse: (val: {
    points: number;
    fiat: number;
    currency?: ApiCurrency;
    currencyExchangeRate?: number;
  }) => void;
}) => {
  const { token } = useContext(AuthContext);
  const [data, setData] = useState<ApiPaymentPoint>();
  const { t } = useTranslation();
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function fetchData() {
    const data = await paymentPoints(
      {
        salesIndividualId: props.salesIndividualId,
        reservedSalesIndividualIds: props.reservedPoints.map(
          (it) => it.salesIndividualId,
        ),
        reservedPoints: props.reservedPoints.map((it) => it.points),
      },
      token,
    );

    const totalPointCurrency = await currencyExchange(
      data.total.currency.code,
      'TRY',
      data.total.amount,
    );

    const currencyExchangeRate =
      data.total.amount > 0
        ? data.total.amount / totalPointCurrency?.amount
        : 0; // 1 TRY => ?€ | ?$...

    setData({ ...data, currencyExchangeRate });
    setLoading(false);
  }

  if (loading) {
    return (
      <Box
        sx={{
          position: 'fixed',
          right: 0,
          left: 800,
          bottom: 0,
          top: 300,
          zIndex: 1002,
        }}
      >
        <CircularProgress color="secondary" />
      </Box>
    );
  }

  return (
    <React.Fragment>
      <Dialog open={props.open} onClose={props.onClose}>
        {loading && <CircularProgress color="secondary" />}
        <DialogTitle>{t('usePointsDialog.title')}</DialogTitle>
        <DialogContent>
          {data && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                maxWidth: '700px',
                minWidth: '480px',
                marginTop: 2,
                marginLeft: 1,
                padding: 1,
              }}
            >
              <PointDisplay
                title={t('usePointsDialog.totalPoints')}
                points={data.total.points}
                fiat={data.total.amount}
                currency={data.total.currency.code}
                currencyExchangeRate={data.currencyExchangeRate}
                imageUrl={totalPointIcon}
                color="success"
              />

              <PointDisplay
                title={t('usePointsDialog.availablePoints')}
                points={data.available.points}
                fiat={data.available.amount}
                currency={data.available.currency.code}
                currencyExchangeRate={data.currencyExchangeRate}
                imageUrl={spentPointIcon}
                color="danger"
              />

              <PointDisplay
                title={t('usePointsDialog.toBeUsed')}
                points={data.toBeUsed.points}
                fiat={data.toBeUsed.amount}
                currency={data.toBeUsed.currency.code}
                currencyExchangeRate={data.currencyExchangeRate}
                imageUrl={remainingPointIcon}
                color="info"
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            color="inherit"
            variant="contained"
            onClick={props.onClose}
            sx={{ mr: 2 }}
          >
            {t('usePointsDialog.cancelBtn')}
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={() =>
              data &&
              props.onUse({
                points: data.toBeUsed.points,
                fiat: data.toBeUsed.amount,
                currency: data.toBeUsed.currency,
                currencyExchangeRate: data.currencyExchangeRate,
              })
            }
          >
            {t('usePointsDialog.usePointsBtn')}
          </Button>
        </DialogActions>
      </Dialog>
    </React.Fragment>
  );
};
